import {Injectable, NgZone} from "@angular/core";
import {
  default as OneSignalPlugin,
  NotificationClickEvent,
  NotificationWillDisplayEvent,
  OSNotificationPermission,
} from "onesignal-cordova-plugin";
import {Observable} from "rxjs";
import {NotificationEventTypeMap} from "onesignal-cordova-plugin/dist/models/NotificationClicked";

type NotificationEventName = keyof NotificationEventTypeMap;

@Injectable()
export class Notifications {
  constructor(private readonly ngZone: NgZone) {
  }

  onClick(): Observable<NotificationClickEvent> {
    return this.addListener('click');
  }

  onForegroundWillDisplay(): Observable<NotificationWillDisplayEvent> {
    return this.addListener('foregroundWillDisplay');
  }

  onPermissionChange(): Observable<boolean> {
    return this.addListener('permissionChange');
  }

  /**
   * Whether this app has push notification permission. Returns true if the user has accepted permissions,
   * or if the app has ephemeral or provisional permission.
   */
  async getPermissionAsync(): Promise<boolean> {
    return this.ngZone.runOutsideAngular(() => new Promise<boolean>((resolve, reject) => {
      OneSignalPlugin.Notifications.getPermissionAsync()
        .then(
          (result) => {
            this.ngZone.run(() => {
              resolve(result);
            });
          },
          (reason) => {
            this.ngZone.run(() => {
              reject(reason);
            });
          },
        );
    }));
  }

  /**
   * iOS Only.
   *
   * Returns the enum for the native permission of the device. It will be one of:
   * OSNotificationPermissionNotDetermined,
   * OSNotificationPermissionDenied,
   * OSNotificationPermissionAuthorized,
   * OSNotificationPermissionProvisional - only available in iOS 12,
   * OSNotificationPermissionEphemeral - only available in iOS 14
   */
  permissionNative(): Promise<OSNotificationPermission> {
    return this.ngZone.runOutsideAngular(() => new Promise<OSNotificationPermission>((resolve, reject) => {
      OneSignalPlugin.Notifications.permissionNative()
        .then(
          (result) => {
            this.ngZone.run(() => {
              resolve(result);
            });
          },
          (reason) => {
            this.ngZone.run(() => {
              reject(reason);
            });
          },
        );
    }));
  }

  /**
   * Prompt the user for permission to receive push notifications. This will display the native system prompt to request push notification permission.
   * Use the fallbackToSettings parameter to prompt to open the settings app if a user has already declined push permissions.
   */
  requestPermission(fallbackToSettings?: boolean): Promise<boolean> {
    return this.ngZone.runOutsideAngular(() => new Promise<boolean>((resolve, reject) => {
      OneSignalPlugin.Notifications.requestPermission(fallbackToSettings)
        .then(
          (result) => {
            this.ngZone.run(() => {
              resolve(result);
            });
          },
          (reason) => {
            this.ngZone.run(() => {
              reject(reason);
            });
          },
        );
    }));
  }

  /**
   * Whether attempting to request notification permission will show a prompt. Returns true if the device has not been prompted for push notification permission already.
   */
  canRequestPermission(): Promise<boolean> {
    return this.ngZone.runOutsideAngular(() => new Promise<boolean>((resolve, reject) => {
      OneSignalPlugin.Notifications.canRequestPermission()
        .then(
          (result) => {
            this.ngZone.run(() => {
              resolve(result);
            });
          },
          (reason) => {
            this.ngZone.run(() => {
              reject(reason);
            });
          },
        );
    }));
  }

  /**
   * iOS Only.
   *
   * Instead of having to prompt the user for permission to send them push notifications, your app can request provisional authorization.
   *
   * For more information: https://documentation.onesignal.com/docs/ios-customizations#provisional-push-notifications
   */
  registerForProvisionalAuthorization(): Promise<boolean> {
    return this.ngZone.runOutsideAngular(() => new Promise<boolean>((resolve) => {
      OneSignalPlugin.Notifications.registerForProvisionalAuthorization((response) => {
        this.ngZone.run(() => {
          resolve(response);
        });
      });
    }));
  }

  /**
   * Removes all OneSignal notifications.
   */
  clearAll(): void {
    OneSignalPlugin.Notifications.clearAll();
  }

  /**
   * Android only.
   *
   * Cancels a single OneSignal notification based on its Android notification integer ID. Use instead of Android's [android.app.NotificationManager.cancel], otherwise the notification will be restored when your app is restarted.
   */
  removeNotification(id: number): void {
    OneSignalPlugin.Notifications.removeNotification(id);
  }

  /**
   * Android only.
   *
   * Cancels a group of OneSignal notifications with the provided group key. Grouping notifications is a OneSignal concept, there is no [android.app.NotificationManager] equivalent.
   */
  removeGroupedNotifications(id: string): void {
    OneSignalPlugin.Notifications.removeGroupedNotifications(id);
  }

  private addListener<K extends NotificationEventName>(event: K): Observable<NotificationEventTypeMap[K]> {
    return this.ngZone.runOutsideAngular(() => new Observable<NotificationEventTypeMap[K]>((subscriber) => {
      const listener = (event: NotificationEventTypeMap[K]) => {
        this.ngZone.run(() => {
          subscriber.next(event);
        });
      }

      OneSignalPlugin.Notifications.addEventListener(event, listener);

      return () => {
        OneSignalPlugin.Notifications.removeEventListener(event, listener);
      };
    }));
  }
}
